import axios from 'axios';
import { COGNITO_CONFIG } from '../constants/cognito';
import { logError } from '../utils/errorHandlers';

/**
 * Authenticates with Cognito using USER_PASSWORD_AUTH flow.
 *
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{idToken?: string, accessToken?: string, expiresIn?: number, challenge?: string, session?: string}>}
 *   On success: { idToken, accessToken, expiresIn }
 *   On NEW_PASSWORD_REQUIRED: { challenge: 'NEW_PASSWORD_REQUIRED', session }
 */
export const loginWithCognito = async (email, password) => {
    const { region, clientId } = COGNITO_CONFIG;

    const endpoint = `https://cognito-idp.${region}.amazonaws.com/`;

    const payload = {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: clientId,
        AuthParameters: {
            USERNAME: email,
            PASSWORD: password
        }
    };

    try {
        const response = await axios.post(endpoint, payload, {
            headers: {
                'Content-Type': 'application/x-amz-json-1.1',
                'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
            }
        });

        // Handle NEW_PASSWORD_REQUIRED challenge
        if (response.data?.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
            return {
                challenge: 'NEW_PASSWORD_REQUIRED',
                session: response.data.Session
            };
        }

        const authResult = response.data?.AuthenticationResult;
        if (!authResult) {
            throw new Error('Authentication failed: no token received');
        }

        return {
            idToken: authResult.IdToken,
            accessToken: authResult.AccessToken,
            expiresIn: authResult.ExpiresIn || 3600
        };
    } catch (error) {
        logError('[authService] Cognito login failed', error);

        if (error?.response?.data?.__type === 'NotAuthorizedException') {
            throw new Error('E-Mail oder Passwort ungültig.');
        }
        if (error?.response?.data?.__type === 'UserNotFoundException') {
            throw new Error('Benutzer nicht gefunden.');
        }
        if (error?.response?.data?.__type === 'PasswordResetRequiredException') {
            throw new Error('Passwort-Zurücksetzung erforderlich. Bitte wende dich an einen Administrator, um dein Passwort zurücksetzen zu lassen.');
        }
        if (error?.response?.data?.message) {
            throw new Error(error.response.data.message);
        }

        throw new Error('Anmeldung fehlgeschlagen. Bitte versuche es später erneut.');
    }
};

/**
 * Completes the NEW_PASSWORD_REQUIRED challenge by setting a new password.
 *
 * @param {string} email - User email
 * @param {string} newPassword - New password
 * @param {string} session - Session token from the challenge response
 * @returns {Promise<{idToken: string, accessToken: string, expiresIn: number}>}
 */
export const completeNewPasswordChallenge = async (email, newPassword, session) => {
    const { region, clientId } = COGNITO_CONFIG;

    const endpoint = `https://cognito-idp.${region}.amazonaws.com/`;

    const payload = {
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: clientId,
        ChallengeResponses: {
            USERNAME: email,
            NEW_PASSWORD: newPassword
        },
        Session: session
    };

    try {
        const response = await axios.post(endpoint, payload, {
            headers: {
                'Content-Type': 'application/x-amz-json-1.1',
                'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'
            }
        });

        const authResult = response.data?.AuthenticationResult;
        if (!authResult) {
            throw new Error('Password change failed: no token received');
        }

        return {
            idToken: authResult.IdToken,
            accessToken: authResult.AccessToken,
            expiresIn: authResult.ExpiresIn || 3600
        };
    } catch (error) {
        logError('[authService] Password change failed', error);

        if (error?.response?.data?.message) {
            throw new Error(error.response.data.message);
        }

        throw new Error('Passwortänderung fehlgeschlagen.');
    }
};

/**
 * Extracts user info from the ID token payload (decodes without verification).
 * Verification is done server-side by the API Lambda.
 *
 * @param {string} idToken - ID token JWT
 * @returns {{email: string, sub: string, groups: string[]}|null}
 */
export const extractUserFromToken = (idToken) => {
    try {
        const parts = idToken.split('.');
        if (parts.length !== 3) {
            return null;
        }
        const payload = JSON.parse(atob(parts[1]));

        return {
            email: payload.email || '',
            sub: payload.sub || '',
            groups: payload['cognito:groups'] || []
        };
    } catch (error) {
        logError('[authService] Failed to extract user from token', error);
        return null;
    }
};

/**
 * Checks if a token is expired based on the stored expiry time.
 *
 * @param {number|undefined} expiresAt - Unix timestamp in milliseconds when the token expires
 * @returns {boolean}
 */
export const isTokenExpired = (expiresAt) => {
    if (!expiresAt) {
        return true;
    }
    return Date.now() >= expiresAt;
};
